import type { Ref } from 'react';
import type { Alignment, Profile, Question, VisitorAnswer } from '@/domain/types';
import { creatorAnswerAsAnswer, describeAnswer } from '@/domain/answers';
import { celebrationFor, ribbonFor } from '@/domain/celebration';
import styles from './journey.module.css';

interface CreatorReplyProps {
  question: Question;
  profile: Profile;
  visitorAnswer: VisitorAnswer;
  /** How the two answers landed. `null` when they cannot be compared. */
  alignment: Alignment | null;
  ref?: Ref<HTMLElement>;
}

/**
 * The reciprocal half of the exchange.
 *
 * Two speech bubbles rather than a two-row table: her answer on her side, his
 * on his, arriving one after the other like two people saying the same thing
 * out loud. The `<dl>` underneath is unchanged, so "You" and "James" are still
 * unambiguous to a screen reader — the personality is entirely in the styling.
 */
export function CreatorReply({
  question,
  profile,
  visitorAnswer,
  alignment,
  ref,
}: CreatorReplyProps) {
  const visitorAnswerText = describeAnswer(question, visitorAnswer);
  const creatorAnswerText = describeAnswer(question, creatorAnswerAsAnswer(question));

  const celebration = celebrationFor(alignment, question);
  const ribbon = ribbonFor(alignment, question);

  const ribbonTone =
    celebration === 'sparkle'
      ? styles.ribbonStrong
      : celebration === 'glow'
        ? styles.ribbonPartial
        : styles.ribbonNeutral;

  return (
    <aside
      ref={ref}
      className={[
        styles.reply,
        celebration === 'sparkle' ? styles.replyAligned : '',
        celebration === 'glow' ? styles.replyAligning : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-live="polite"
    >
      <p className={styles.replyHead}>
        <span>How we answered</span>
        {ribbon ? (
          <span className={`${styles.ribbon} ${ribbonTone}`}>
            <span aria-hidden="true">{ribbon.emoji}</span>
            {ribbon.label}
          </span>
        ) : null}
      </p>

      <dl className={styles.replyComparison}>
        <div className={`${styles.replyPerson} ${styles.replyPersonYou}`}>
          <dt>You</dt>
          <dd>{visitorAnswerText}</dd>
        </div>
        <div className={`${styles.replyPerson} ${styles.replyPersonCreator}`}>
          <dt>{profile.displayName}</dt>
          <dd>{creatorAnswerText}</dd>
        </div>
      </dl>

      <p className={styles.replyNote}>{question.creatorNote}</p>
    </aside>
  );
}
